import { getDevAuthHeaders } from '../auth/dev-impersonation';
import { getNativeBearerAuthHeaders } from '../auth/native-api-auth';
import { getApiBaseUrl } from '../env';

export type ConnectionScope = 'read' | 'write';

export type ConnectionRecord = {
  id: string;
  name: string | null;
  start: string | null;
  scope: ConnectionScope;
  expiresAt: string | null;
  createdAt: string | null;
  memberId: string;
  memberName?: string | null;
};

export type CreatedConnection = {
  token: string;
  endpoint: string;
  connection: {
    id: string;
    name: string | null;
    start: string | null;
    scope: ConnectionScope;
    expiresAt: string | null;
  };
};

async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  const base = getApiBaseUrl().replace(/\/$/, '');
  return fetch(`${base}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...getDevAuthHeaders(),
      ...(await getNativeBearerAuthHeaders()),
      ...init?.headers,
    },
  });
}

function parseErrorMessage(text: string, status: number): string {
  try {
    const body = JSON.parse(text) as { error?: { message?: string } };
    if (body.error?.message) {
      return body.error.message;
    }
  } catch {
    // Response was not JSON.
  }
  return text || `Request failed (${status})`;
}

async function parseJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
  return res.json() as Promise<T>;
}

export async function fetchConnections(): Promise<ConnectionRecord[]> {
  const res = await apiFetch('/rpc/connections');
  return (await parseJson<{ connections: ConnectionRecord[] }>(res)).connections;
}

export async function createConnection(input: {
  name: string;
  scope: ConnectionScope;
  expiresInDays: number;
}): Promise<CreatedConnection> {
  const res = await apiFetch('/rpc/connections', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return parseJson<CreatedConnection>(res);
}

export async function revokeConnection(id: string): Promise<void> {
  const res = await apiFetch(`/rpc/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(parseErrorMessage(text, res.status));
  }
}
